import { Flex, HStack, IconButton, Text, VStack } from "@chakra-ui/react"
import { useEffect } from "react"
import {
  HomeResourceCopyConfigLinkFragment,
  ResourceCopyConfigCopyBehavior,
  ResourceCopyConfigFragment,
  useDeferOrExecuteResourceCopyConfigMutation,
  useDeleteResourceCopyConfigLinkMutation,
  useHomeResourceCopyConfigLinkConnectionsQuery,
  useResourceCopyConfigConnectionsQuery
} from "@pathwright/graphql-client/src/generated"
import Pathicon from "../../../pathicon/Pathicon"
import { usePathwrightContext } from "../../../pathwright/PathwrightContext"
import ResourceCopyConfigItem from "../../../resource/copy-config/ResourceCopyConfigItem"
import { flattenResourceCopyConfigs } from "../../../resource/copy-config/ResourceCopyConfigItems"
import { useHomeRegistrationVars } from "../../registration/HomeRegistrationQueryVars"

type HomeSuperUserAugmentationPathCopyItemProps = {
  resourceCopyConfig: ResourceCopyConfigFragment
  resourceCopyConfigLink?: HomeResourceCopyConfigLinkFragment
}

function HomeSuperUserAugmentationPathCopyItem({
  resourceCopyConfig,
  resourceCopyConfigLink
}: HomeSuperUserAugmentationPathCopyItemProps) {
  const pwContext = usePathwrightContext()
  const { positioningContext } = useHomeRegistrationVars()

  const [copyMutation, copyMutationState] =
    useDeferOrExecuteResourceCopyConfigMutation({
      // Refetch the HomeResourceCopyConfigLinkConnections so new link
      // appears on Home Screen.
      refetchQueries: ["HomeResourceCopyConfigLinkConnections"]
    })

  const [deleteResourceCopyConfigLink] =
    useDeleteResourceCopyConfigLinkMutation({
      variables: {
        id: resourceCopyConfigLink?.id!
      }
    })

  // Poll for resource copy config to be deleted.
  const resourceCopyConfigLinkConnectionsQuery =
    useHomeResourceCopyConfigLinkConnectionsQuery({
      variables: {
        first: 1042,
        copiedTimeDateFilter: { neq: null },
        positioningContext: positioningContext!
      },
      pollInterval: 1000,
      skip: !resourceCopyConfigLink || !positioningContext
    })

  useEffect(() => {
    resourceCopyConfigLinkConnectionsQuery.stopPolling()
  }, [resourceCopyConfigLink])

  return (
    <ResourceCopyConfigItem
      key={resourceCopyConfig.id}
      item={resourceCopyConfig}
      renderInternal={(config) => (
        <Flex ml="auto">
          <IconButton
            aria-label="create copy path link"
            p={0}
            boxSize="1.6em"
            minW={0}
            children={<Pathicon icon="plus" pos="absolute" size="1em" />}
            loading={copyMutationState.loading}
            onClick={async () => {
              if (resourceCopyConfigLink) {
                const result = await deleteResourceCopyConfigLink()
                resourceCopyConfigLinkConnectionsQuery.startPolling(1000)
                window.open(result.data?.deleteResourceCopyConfigLink!)
              } else {
                await copyMutation({
                  variables: {
                    copyCode: config.lookup_key,
                    targetSchoolId: pwContext.school.id
                  }
                })
                // Refetch the resource copy config link connections to update the
                // resourceCopyConfigLink prop once again.
                resourceCopyConfigLinkConnectionsQuery.refetch()
              }
            }}
          />
        </Flex>
      )}
    />
  )
}

function HomeSuperUserAugmentationPathCopy() {
  const { positioningContext } = useHomeRegistrationVars()
  const resourceCopyConfigConnectionsQuery =
    useResourceCopyConfigConnectionsQuery({
      variables: {
        first: 1042,
        fromSpaceId: 7888
      }
    })

  const resourceCopyConfigLinkConnectionsQuery =
    useHomeResourceCopyConfigLinkConnectionsQuery({
      variables: {
        first: 1042,
        copiedTimeDateFilter: { neq: null },
        positioningContext: positioningContext!
      },
      skip: !positioningContext
    })

  // Used to assess if a resource copy config has already been linked.
  const copyLinks =
    resourceCopyConfigLinkConnectionsQuery.data?.resourceCopyConfigLinkConnections?.edges?.map(
      (edge) => edge?.node!
    ) || []

  const resourceCopyConfigConnections =
    resourceCopyConfigConnectionsQuery.data?.resourceCopyConfigConnections?.edges?.map(
      (edge) => edge!.node!
    ) || []

  // Render a flat list, filtering out configs that can't be deferred.
  const resourceCopyConfigs = flattenResourceCopyConfigs(
    resourceCopyConfigConnections
  ).filter(
    (config) =>
      config.copy_behavior === ResourceCopyConfigCopyBehavior.Deferred &&
      config.allow_copy_via_link
  )

  return resourceCopyConfigs.length ? (
    <VStack w="100%">
      <HStack w="100%" fontSize="xs">
        <Pathicon fontSize="xs" icon="plus" />
        <Text fontSize="xs" fontWeight="normal" m={0} textAlign="left">
          Prompts:
        </Text>
      </HStack>
      <VStack w="100%">
        {resourceCopyConfigs.map((resourceCopyConfig) => (
          <HomeSuperUserAugmentationPathCopyItem
            key={resourceCopyConfig.id}
            resourceCopyConfig={resourceCopyConfig}
            resourceCopyConfigLink={copyLinks.find(
              (copyLink) =>
                copyLink.resourceCopyConfig.id === resourceCopyConfig.id
            )}
          />
        ))}
      </VStack>
    </VStack>
  ) : null
}

export default HomeSuperUserAugmentationPathCopy
